/**
 * Lightweight toast layer built on ICDS `ic-toast` / `ic-toast-region`.
 *
 * `ic-toast` does not show on mount — it must be made visible via its
 * `setVisible()` method. We do that through a ref once each toast is mounted,
 * then auto-dismiss handling removes it from state.
 *
 * Usage:
 *   const toast = useToast();
 *   toast.success("Device approved");
 *   toast.error("Reboot failed", "The agent did not respond.");
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { IcToast, IcToastRegion } from "@ukic/react";

type ToastVariant = "success" | "error" | "warning" | "info" | "neutral";

interface ToastItem {
  id: number;
  heading: string;
  message?: string;
  variant: ToastVariant;
}

interface ToastApi {
  push: (heading: string, opts?: { message?: string; variant?: ToastVariant }) => void;
  success: (heading: string, message?: string) => void;
  error: (heading: string, message?: string) => void;
  warning: (heading: string, message?: string) => void;
  info: (heading: string, message?: string) => void;
}

const ToastCtx = createContext<ToastApi | null>(null);

let nextId = 1;

/** Wrapper that calls setVisible() on the underlying web component when mounted. */
function ToastView({
  item,
  onRemove,
}: {
  item: ToastItem;
  onRemove: (id: number) => void;
}) {
  const setRef = useCallback((el: HTMLIcToastElement | null) => {
    // Stencil exposes setVisible() as an async method; call once mounted.
    el?.setVisible?.();
  }, []);

  return (
    <IcToast
      ref={setRef}
      heading={item.heading}
      message={item.message}
      variant={item.variant}
      dismissMode="automatic"
      autoDismissTimeout={5000}
      onIcDismiss={() => onRemove(item.id)}
    />
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);

  const remove = useCallback((id: number) => {
    setItems((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const push = useCallback<ToastApi["push"]>(
    (heading, opts) => {
      const id = nextId++;
      setItems((cur) => [
        ...cur,
        { id, heading, message: opts?.message, variant: opts?.variant ?? "info" },
      ]);
      // Safety net: clean up our state shortly after the toast self-dismisses.
      window.setTimeout(() => remove(id), 6500);
    },
    [remove],
  );

  const apiValue = useMemo<ToastApi>(
    () => ({
      push,
      success: (h, m) => push(h, { message: m, variant: "success" }),
      error: (h, m) => push(h, { message: m, variant: "error" }),
      warning: (h, m) => push(h, { message: m, variant: "warning" }),
      info: (h, m) => push(h, { message: m, variant: "info" }),
    }),
    [push],
  );

  return (
    <ToastCtx.Provider value={apiValue}>
      {children}
      <IcToastRegion>
        {items.map((t) => (
          <ToastView key={t.id} item={t} onRemove={remove} />
        ))}
      </IcToastRegion>
    </ToastCtx.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}
